<template>
  <b-modal 
    id="welcome-discount-modal" 
    title="Edit Welcome Discount" 
    @show="handleShowModal" 
    centered
  >
    <b-container>
      <b-row class="py-4 border-bottom">
        <b-col md="4" class="primary-label">
          Basic Details
        </b-col>
        <b-col md="8">
          <b-form-group 
            :state="validateField('name')"
            :invalid-feedback="getErrorMessage('name')"
          >
            <template slot="label">
              <span class="secondary-label mr-1">Welcome Discount Name</span>
              <i class="ic-tooltip" v-b-popover.hover="welcomeDiscountNamePopoverContent"></i>
            </template>
            <b-form-input 
              v-model="form.name" 
              placeholder="Welcome Discount" 
              :maxlength="welcomeDiscountNameMaxLength" 
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="py-4 border-bottom">
        <b-col md="4" class="primary-label">
          Value
        </b-col>
        <b-col md="8">
          <b-row>
            <b-col>
              <b-form-group
                id="value-type"
                label="Type"
                label-class="secondary-label"
                class="mb-3"
              >
                <b-form-select
                  :options="valueTypeOptions"
                  v-model="form.value_type"
                  @change="form.value=''"
                />
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group
                label="Amount"
                label-class="secondary-label"
                :state="validateField('value')"
                :invalid-feedback="getErrorMessage('value')"
              >
                <template v-if="form.value_type === '%'">
                  <PercentageInput v-model="form.value"/>
                </template>
                <template v-else>
                  <CurrencyAmountInput 
                    v-model="form.value" 
                    max-length="6" 
                  />
                </template>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row class="py-4 border-bottom">
        <b-col md="4" class="primary-label">
          Terms and conditions
        </b-col>
        <b-col md="8">
          <b-form-group>
            <div class="secondary-label mb-2">
              <span class="mr-1">Terms and conditions</span>
              <span class="text-muted">(Optional)</span>
            </div>
            <vue-editor 
              ref="termsAndConditionsEditor" 
              v-model="form.terms_and_conditions" 
              :editorToolbar="customToolbar" 
              @text-change="handleTermsAndConditionsChange"
            />
            <div v-if="$refs.termsAndConditionsEditor" class="d-flex justify-content-end">
              <small id="terms-and-conditions-char-count" class="text-muted mt-1">
                {{ termsAndConditionsCount }}/{{ termsAndConditionsMaxLength }}
              </small>
            </div>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="pt-4">
        <b-col md="4" class="primary-label">
          Visibility
        </b-col>
        <b-col md="8">
          <switches
            v-model="form.published"
            theme="custom"
            color="primary"
            type-bold="true"
            text-disabled="Off"
            text-enabled="On"
          />
        </b-col>
      </b-row>
    </b-container>
    <div slot="modal-footer">
      <b-btn variant="outline-primary" @click="hideModal">
          Cancel
      </b-btn>
      <b-btn variant="primary" @click="saveChanges">
          Save Changes
      </b-btn>
        </div>
  </b-modal>
</template>

<script>
import { defineComponent, ref, computed } from "@vue/composition-api";
import { useVuelidate } from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import CurrencyAmountInput from "@/components/common/CurrencyAmountInput";
import PercentageInput from "@/components/common/PercentageInput";
import Switches from "vue-switches";
import { VueEditor } from "vue2-editor";
import { greaterThanZero } from "@/validators";
import notification from "@/services/notification";

export default defineComponent({
    components: {
        CurrencyAmountInput,
        PercentageInput,
        Switches,
        VueEditor
    },
    setup(_, { root }) {
        const form = ref({
            name: "",
            value: "",
            value_type: "php",
            terms_and_conditions: "",
            published: false
        });
        const valueTypeOptions = ref([
            { value: "php", text: "Php" },
            { value: "%", text: "%" }
        ]);
        const customToolbar = ref([
            ["bold", "italic", "underline"],
            [
                { align: "" },
                { align: "center" },
                { align: "right" },
                { align: "justify" },
                { list: "ordered" },
                { list: "bullet" }
            ]
        ]);
        const welcomeDiscountNamePopoverContent =
            "This is the name of the welcome offer that your customers will see.";
        const welcomeDiscountNameMaxLength = ref(18);
        const termsAndConditionsMaxLength = ref(3000);
        const welcomeDiscountDetails = computed(
            () => root.$store.state.achievements.welcomeDiscountDetails || null
        );
        const fieldRequiredErrorMessage = "This field is required.";
        const rules = {
            name: {
                required: helpers.withMessage(
                    fieldRequiredErrorMessage,
                    required
                )
            },
            value: {
                required: helpers.withMessage(
                    fieldRequiredErrorMessage,
                    required
                ),
                greaterThanZero: helpers.withMessage(
                    "Must be greater than 0.",
                    greaterThanZero
                )
            }
        };
        const v$ = useVuelidate(rules, form);
        const validateField = field =>
            v$.value[field].$dirty ? !v$.value[field].$error : null;
        const hasError = field => !!v$.value[field].$errors.length;
        const getErrorMessage = field =>
            hasError(field) ? v$.value[field].$errors[0].$message : "";
        const handleShowModal = async () => {
            v$.value.$reset();
            await root.$store.dispatch(
                "achievements/getWelcomeDiscountDetails"
            );

            if (welcomeDiscountDetails.value) {
                form.value.name = welcomeDiscountDetails.value.name;
                form.value.value = welcomeDiscountDetails.value.value;
                form.value.terms_and_conditions =
                    welcomeDiscountDetails.value.terms_and_conditions;
                form.value.published = Boolean(
                    welcomeDiscountDetails.value.published
                );
                form.value.value_type =
                    welcomeDiscountDetails.value.value_type || "php";
            }
        };
        const hideModal = () => {
            root.$emit("bv::hide::modal", "welcome-discount-modal");
        };
        const saveChanges = async () => {
            v$.value.$touch();

            if (v$.value.$invalid) {
                notification.warning("Please complete all required fields");
            } else {
                await root.$store.dispatch(
                    "achievements/saveWelcomeDiscountDetails",
                    form.value
                );
                await root.$store.dispatch("achievements/getList", 1);
                hideModal();
            }
        };

        return {
            form,
            valueTypeOptions,
            customToolbar,
            welcomeDiscountNamePopoverContent,
            welcomeDiscountNameMaxLength,
            termsAndConditionsMaxLength,
            validateField,
            hasError,
            getErrorMessage,
            handleShowModal,
            hideModal,
            saveChanges
        };
    },
    computed: {
        termsAndConditionsCount() {
            return this.$refs.termsAndConditionsEditor.quill.getLength() - 1;
        }
    },
    methods: {
        handleTermsAndConditionsChange() {
            if (
                !this.form.terms_and_conditions.replace(/<[^>]*>?/gm, "").trim()
            ) {
                this.$refs.termsAndConditionsEditor.quill.deleteText(0, 1);
            } else if (
                this.termsAndConditionsCount > this.termsAndConditionsMaxLength
            ) {
                this.$refs.termsAndConditionsEditor.quill.deleteText(
                    this.termsAndConditionsMaxLength,
                    this.termsAndConditionsCount
                );
            }
        }
    }
});
</script>

<style lang="scss">
#welcome-discount-modal {
    .modal-dialog {
        max-width: 758px;
    }
    .modal-title {
        &::after {
            background: none;
        }
    }
    .modal-header {
        border-bottom: 1px solid #dee2e6;
    }
    .modal-content {
        padding-bottom: 0;
    }
    .modal-footer {
        padding-top: 15px;
        padding-bottom: 15px;
        justify-content: flex-end;
        border-top: 1px solid #dee2e6;
    }
    .close {
        color: #2a2a2a;
        opacity: 1;
        top: 22px;
    }
    .primary-label {
        font-size: 15px;
        font-weight: normal;
        color: #2f3941;
        font-family: MainFontBold, sans-serif;
    }
    .secondary-label {
        font-size: 14px;
        color: #2b2f38;
        font-family: MainFontRegular, sans-serif;
    }
    .form-control,
    .custom-select,
    .vue-switcher__label,
    .ic-tooltip,
    #terms-and-conditions-char-count {
        font-family: MainFontRegular, sans-serif;
    }
    .input-group-text {
        background-color: #ffffff;
    }
    .btn {
        height: 44px;
        width: 177px;
        margin-left: 10px;
        margin-right: 10px;
    }
    .btn-outline-primary:active {
        background: transparent;
        color: #ff8828;
    }
    .vue-switcher {
        top: -20px;
    }
}
</style>
